/**
 * espionage.ts — v0M5.4 (2026-07-25).
 *
 * Runs each world tick per systems.ts ordering. Two responsibilities:
 *
 *   1. Advance `infiltrationDays` for every Spy that's inside a
 *      settlement (`insideSettlementId !== null`). Increment is
 *      `1 / TICKS_PER_GAME_DAY` per tick so a full day = +1.0.
 *      Consumer (settlement panel) reads this to render the
 *      infiltration ladder progress bar per ESPIONAGE.md §2.2.
 *
 *   2. Counter-Espionage roll (ESPIONAGE.md §2.3). Once per game-day,
 *      for every Spy performing CE inside a friendly settlement,
 *      for every enemy Invisibility-tagged entity present in the
 *      same settlement (not on their own team), roll
 *      `counter_espionage_chance` from the CE spy's SpecialUnits row.
 *      On success: kill the enemy spy (delete from state.spies).
 *      Rolls fire at the day-boundary tick only (`tick % TPD === 0`)
 *      to give deterministic, day-cadenced results independent of
 *      tick jitter.
 *
 * Determinism: iteration order via `sortedIds(state.spies)` for both
 * loops. RNG threaded from the sim (per MECHANICS.md §1.1); each roll
 * is a `nextFloat()` compared against the threshold.
 */

import type { Command } from './commands';
import type { GameState, FactionId, SpyId } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import { sortedIds, TICKS_PER_GAME_DAY } from './state';
import { nextFloat } from './rng';
import { loadSpecialUnits, specialUnitHasAbility } from '../data/special-units';
// v0M5.5 (2026-07-25) — Steal drain reads per-province gold accrual.
import { computeProvinceGoldPerDay } from './economy';
// v0M5.5 dials (Sabotage completion timer + Steal growth curve).
import {
  STEAL_BASE_PCT,
  STEAL_INCREMENT_PCT,
  STEAL_INCREMENT_DAYS,
} from './apply-commands';

const ESPIONAGE_SPECIAL_UNITS = loadSpecialUnits();

export function espionageSystem(
  state: GameState,
  _commands: readonly Command[],
  rng: RngState,
  _context: SimContext,
): GameState {
  if (!state.spies || state.spies.size === 0) return state;
  const dayIncrement = 1 / TICKS_PER_GAME_DAY;
  const dayBoundary = state.tick % TICKS_PER_GAME_DAY === 0;

  // Step 1 — infiltration + Steal counter tick. Both counters advance
  // continuously per tick so that the settlement panel can render
  // smooth progress bars without waiting for day boundaries.
  for (const spyId of sortedIds(state.spies)) {
    const spy = state.spies.get(spyId as SpyId);
    if (!spy) continue;
    if (!spy.insideSettlementId) continue;
    spy.infiltrationDays = (spy.infiltrationDays ?? 0) + dayIncrement;
    if (spy.stealActive) {
      spy.stealActiveDays = (spy.stealActiveDays ?? 0) + dayIncrement;
    }
  }

  // Step 2+ — Counter-Espionage + Steal drain + Sabotage countdown.
  // All fire at the day boundary for clean per-day cadence.
  if (!dayBoundary) return state;

  // Step 2 — Steal drain. Each active-Steal Spy siphons `pct` of its
  // hosting settlement's per-day gold income from the enemy faction
  // to the Spy's owning faction. Multiple Steal spies in the same
  // enemy settlement stack (each takes the same % of the same base
  // — deliberate at v1, playtest-tuning question).
  for (const spyId of sortedIds(state.spies)) {
    const spy = state.spies.get(spyId as SpyId);
    if (!spy || !spy.stealActive || !spy.insideSettlementId) continue;
    const settlement = state.settlements.get(spy.insideSettlementId);
    if (!settlement) continue;
    const province = state.provinces.get(settlement.provinceId);
    if (!province) continue;
    // Skip if the settlement fell back to Spy's own faction between ticks
    // (e.g. player captured it — Steal auto-becomes a no-op).
    if (province.ownerFactionId === null) continue;
    if (province.ownerFactionId === spy.ownerFactionId) continue;
    const daysActive = spy.stealActiveDays ?? 0;
    const tier = Math.floor(daysActive / STEAL_INCREMENT_DAYS);
    const pct = Math.min(1.0, STEAL_BASE_PCT + STEAL_INCREMENT_PCT * tier);
    const provinceGold = computeProvinceGoldPerDay(state, province.id);
    if (provinceGold <= 0) continue;
    const drain = provinceGold * pct;
    const enemyFaction = state.factions.get(province.ownerFactionId);
    const spyFaction = state.factions.get(spy.ownerFactionId);
    if (!enemyFaction || !spyFaction) continue;
    // Can't take more than the enemy actually has in the treasury.
    const applied = Math.min(drain, enemyFaction.resources.gold);
    enemyFaction.resources.gold -= applied;
    spyFaction.resources.gold += applied;
  }

  // Step 3 — Sabotage countdown. Decrement one day; on ≤ 0, destroy
  // the target structure + clear the order + emit event log entry.
  for (const spyId of sortedIds(state.spies)) {
    const spy = state.spies.get(spyId as SpyId);
    if (!spy) continue;
    if (spy.sabotageTargetStructureIndex == null || spy.sabotageDaysRemaining == null) continue;
    if (!spy.insideSettlementId) {
      // Not inside — silently clear (defensive; exit_settlement_spy
      // should already have cleared this).
      spy.sabotageTargetStructureIndex = null;
      spy.sabotageDaysRemaining = null;
      continue;
    }
    spy.sabotageDaysRemaining -= 1;
    if (spy.sabotageDaysRemaining > 0) continue;
    // Fire the sabotage.
    const settlement = state.settlements.get(spy.insideSettlementId);
    const province = settlement ? state.provinces.get(settlement.provinceId) : undefined;
    if (province && spy.sabotageTargetStructureIndex >= 0
        && spy.sabotageTargetStructureIndex < province.structures.length) {
      const destroyed = province.structures[spy.sabotageTargetStructureIndex]!;
      province.structures.splice(spy.sabotageTargetStructureIndex, 1);
      // Event log — v0M5.5 uses console for now; wire into the game
      // event log surface when it lands.
      if (typeof console !== 'undefined' && typeof console.info === 'function') {
        console.info(
          `[sabotage] ${destroyed.name} destroyed in settlement ${spy.insideSettlementId} `
          + `(owner faction ${province.ownerFactionId}, by spy ${spy.id})`,
        );
      }
    }
    spy.sabotageTargetStructureIndex = null;
    spy.sabotageDaysRemaining = null;
  }

  // Step 4 — Counter-Espionage kills.
  const kills: SpyId[] = [];
  for (const ceSpyId of sortedIds(state.spies)) {
    const ceSpy = state.spies.get(ceSpyId as SpyId);
    if (!ceSpy || !ceSpy.counterEspionage || !ceSpy.insideSettlementId) continue;
    const ceSpec = ESPIONAGE_SPECIAL_UNITS[ceSpy.specialUnitType];
    if (!ceSpec) continue;
    const chance = ceSpec.counterEspionageChance;
    if (chance <= 0) continue;

    // Enumerate enemy invisible entities in the same settlement.
    for (const targetId of sortedIds(state.spies)) {
      const target = state.spies.get(targetId as SpyId);
      if (!target) continue;
      if (target.insideSettlementId !== ceSpy.insideSettlementId) continue;
      if (!spiesAreEnemies(state, ceSpy.ownerFactionId, target.ownerFactionId)) continue;
      const targetSpec = ESPIONAGE_SPECIAL_UNITS[target.specialUnitType];
      if (!targetSpec) continue;
      if (!specialUnitHasAbility(targetSpec, 'Invisibility')) continue;

      // Roll.
      const roll = nextFloat(rng);
      if (roll < chance) {
        kills.push(target.id);
      }
    }
  }
  // Apply kills after iteration to avoid mutating during traversal.
  for (const killId of kills) {
    state.spies.delete(killId);
  }
  return state;
}

/**
 * Two factions are "enemies" for CE purposes iff they belong to
 * different teams. Same-team factions (§7 team-vision) don't roll
 * against each other. Neutral (teamId < 0) faction spies — if any —
 * also count as enemies to any non-neutral team.
 */
function spiesAreEnemies(state: GameState, aFactionId: FactionId, bFactionId: FactionId): boolean {
  if (aFactionId === bFactionId) return false;
  const a = state.factions.get(aFactionId);
  const b = state.factions.get(bFactionId);
  if (!a || !b) return false;
  return a.teamId !== b.teamId;
}
